/**
 * SND-16242: Desktop inline search shell.
 *
 * Owns only the open/close state of the suggestions popover; the actual search
 * (fetch, morph, keyboard navigation) is handled by the theme's
 * predictive-search-component that lives inside this element.
 */
class SearchInline extends HTMLElement {
  // SND-16780: the panel is now a wide, scrollable grid — show the whole page
  // of suggestions the Predictive Search API returns (its cap is 10).
  static MAX_PRODUCTS = 12;

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const input = this.#input;
    if (!input) return;

    // The theme's predictive-search component calls searchInput.focus() on its own
    // (e.g. after loading "recently viewed" on page load). Only a real user gesture
    // may open the popover: pointer on the field, Tab into it, typing, or a shortcut.
    // A programmatic focus without intent is undone so the field never looks "active".
    let intent = false;
    const armIntent = () => {
      intent = true;
      setTimeout(() => (intent = false), 400);
    };

    input.addEventListener('pointerdown', armIntent, { signal });
    input.addEventListener(
      'focus',
      () => {
        if (this.hasAttribute('data-open')) return;
        if (intent) {
          this.open();
        } else {
          input.blur();
        }
      },
      { signal }
    );
    input.addEventListener('input', () => this.open(), { signal });

    // Click/tap outside closes; clicks inside (results, pills, footer) keep it open
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.hasAttribute('data-open')) return;
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.close();
      },
      { signal }
    );

    // Tabbing out of the component closes it
    this.addEventListener(
      'focusout',
      (event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && this.contains(next)) return;
        if (next === null) return; // window blur / click on non-focusable inside: pointerdown decides
        this.close();
      },
      { signal }
    );

    // Global shortcuts: ⌘K / Ctrl+K, ⌘F / Ctrl+F, or "/" outside form fields.
    // Capture phase so the modal's own ⌘K listener does not also fire.
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.hasAttribute('data-open')) {
          // SND-20229 header v2: Esc closes the panel but keeps the typed query
          // (predictive-search would reset it on the input's own keydown)
          // (and type=search inputs clear themselves on Esc as a default action)
          if (this.hasAttribute('data-hv2')) {
            event.stopPropagation();
            event.preventDefault();
          }
          this.close();
          input.blur();
          return;
        }

        if (event.key === 'Tab' && !this.hasAttribute('data-open')) {
          armIntent(); // Tab landing on the field counts as user intent
          return;
        }

        // Quiet shortcuts (not advertised in the UI): ⌘K / Ctrl+K, ⌘F / Ctrl+F, "/" outside form fields.
        // A bare "f" is never a shortcut: it hijacked typing in forms (SND-17455).
        const plainKey = !event.metaKey && !event.ctrlKey && !event.altKey;
        const modKey = (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey;
        const isShortcut =
          (modKey && (event.key.toLowerCase() === 'k' || event.key.toLowerCase() === 'f')) ||
          (plainKey && event.key === '/' && !this.#isTypingContext());

        if (!isShortcut || !this.#isVisible()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.open();
        input.focus();
        input.select();
      },
      { signal, capture: true }
    );

    // Safety cap only: trimming the DOM (not just hiding) keeps predictive-search
    // keyboard navigation in sync with what is on screen.
    const results = this.querySelector('.search-inline__results');
    if (results) {
      const trim = () => {
        for (const list of results.querySelectorAll('.predictive-search-results__wrapper-products')) {
          const cards = list.querySelectorAll('.predictive-search-results__card--product');
          for (let i = SearchInline.MAX_PRODUCTS; i < cards.length; i++) cards[i].remove();
        }
      };
      trim();
      new MutationObserver(trim).observe(results, { childList: true, subtree: true });
    }

    // Close when the header hides on scroll so the popover never floats alone
    window.addEventListener(
      'scroll',
      () => {
        if (!this.hasAttribute('data-open')) return;
        const rect = this.getBoundingClientRect();
        if (rect.bottom < 0) {
          this.close();
          input.blur();
        }
      },
      { signal, passive: true }
    );

    if (this.dataset.panel === 'b') this.#setupPanelB(input, signal);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #input() {
    return this.querySelector('input.search-inline__input');
  }

  get #popover() {
    return this.querySelector('.search-inline__popover');
  }

  #isVisible() {
    return this.offsetParent !== null;
  }

  #isTypingContext() {
    // Descend into open shadow roots: app-embedded forms report only their host as activeElement.
    let el = document.activeElement;
    while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
    if (!el) return false;
    return (
      el instanceof HTMLIFrameElement ||
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement ||
      el.isContentEditable
    );
  }

  /* ---- SND-17470: variant B panel ------------------------------------ */
  /**
   * Recent searches + best-match add-to-cart live in assets/snd-search-panel.js
   * (shared with the mobile modal); here only the popover placement.
   * @param {HTMLInputElement} input
   * @param {AbortSignal} signal
   */
  #setupPanelB(input, signal) {
    const results = this.querySelector('.search-inline__results');
    if (!results) return;

    const moduleUrl = this.dataset.sndPanelJs;
    if (moduleUrl) {
      import(moduleUrl)
        .then(({ setupPanel }) => setupPanel({ root: this, input, results, signal, onAdded: () => this.close() }))
        .catch((error) => console.warn('[search-inline] panel module', error));
    }

    // Stretch the panel to the right page margin so the best-match column sits
    // under the field (variant B), recomputed on every open and on resize.
    // SND-20852: --snd-sp-edge is the same distance to the page margin; the
    // header v2 sticky compact bar hangs the panel off it, because the field
    // there sits left of the account/cart icons, not at the page edge.
    const place = () => {
      const rect = this.getBoundingClientRect();
      const shift = Math.max(0, window.innerWidth - 24 - rect.right);
      const margin = parseFloat(getComputedStyle(this).getPropertyValue('--page-margin')) || 40;
      const edge = Math.max(0, window.innerWidth - margin - rect.right);
      this.style.setProperty('--snd-sp-shift', `${Math.round(shift)}px`);
      this.style.setProperty('--snd-sp-edge', `${Math.round(edge)}px`);
    };
    this.addEventListener('pointerdown', place, { signal, capture: true });
    input.addEventListener('focus', place, { signal });
    window.addEventListener('resize', place, { signal, passive: true });
    // The header switches to/from the compact bar on scroll while the panel may be open
    let frame = 0;
    window.addEventListener(
      'scroll',
      () => {
        if (frame || !this.hasAttribute('data-open')) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          place();
        });
      },
      { signal, passive: true }
    );
    place();
  }

  open() {
    if (this.hasAttribute('data-open')) return;
    this.setAttribute('data-open', '');
    // SND-17518: the page behind the panel blurs (CSS on html[data-snd-search-open])
    document.documentElement.setAttribute('data-snd-search-open', '');
    this.#input?.setAttribute('aria-expanded', 'true');
    this.#popover?.setAttribute('aria-hidden', 'false');
  }

  close() {
    if (!this.hasAttribute('data-open')) return;
    this.removeAttribute('data-open');
    document.documentElement.removeAttribute('data-snd-search-open');
    this.#input?.setAttribute('aria-expanded', 'false');
    this.#popover?.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('search-inline')) {
  customElements.define('search-inline', SearchInline);
}
